import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { useToast } from '@/hooks/use-toast';
import { X, ChevronDown, ChevronUp, Tag, FileText, Folder } from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Switch } from '@/components/ui/switch';
import { EnhancedTagsInput } from '../shared/EnhancedTagsInput';
import { EnhancedChatbotInput } from '../shared/EnhancedChatbotInput';
import { EnhancedCopilotInput } from '../shared/EnhancedCopilotInput';
import { MultiSelectComboBox } from '../shared/MultiSelectComboBox';
import { selectAllFolders } from '@/store/slices/folder/folderSelectors';
import { Content, ContentStatus } from '@/types/content';
import { createSnippet, fetchContents } from '@/store/slices/content/contentSlice';
import { selectTags, fetchTags } from '@/store/slices/tagsSlice';
import { selectChatbots, fetchChatbotsIfNeeded } from '@/store/slices/chatbots/chatbotsSlice';
import { selectAllProfiles, fetchCopilotProfiles } from '@/store/slices/copilot-profiles/copilotProfilesSlice';

interface CreateSnippetModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const CreateSnippetModal: React.FC<CreateSnippetModalProps> = ({
  open,
  onOpenChange
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [content, setContent] = useState('');
  const [tags, setTags] = useState<string[]>([]);
  const [isLive, setIsLive] = useState(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState(false);
  const [linkedChatbots, setLinkedChatbots] = useState<string[]>([]);
  const [linkedCopilots, setLinkedCopilots] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [folderId, setFolderId] = useState<string | null>('all'); // Default to 'all' instead of null
  
  const dispatch = useAppDispatch();
  const { toast } = useToast();
  const folders = useAppSelector(selectAllFolders);
  const availableTags = useAppSelector(selectTags);
  const availableChatbots = useAppSelector(selectChatbots).map(chatbot => ({
    id: chatbot.id,
    name: chatbot.name
  }));
  const availableCopilots = useAppSelector(selectAllProfiles).map(copilot => ({
    id: copilot.id,
    name: copilot.name
  }));

  // Fetch tags, chatbots, and copilots when modal opens
  useEffect(() => {
    if (open) {
      dispatch(fetchTags({
        searchQuery: '',
        filterEntity: 'all',
        sortField: 'name',
        sortDirection: 'asc',
        page: 1,
        limit: 1000
      }));
      dispatch(fetchChatbotsIfNeeded());
      dispatch(fetchCopilotProfiles());
    }
  }, [open, dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    
    
    if (!title || !content) {
      toast({
        title: "Required fields missing",
        description: "Please fill in all required fields",
        variant: "destructive"
      });
      return;
    }

    const contentData: Content = {
      title,
      content,
      type: 'snippet' as const,
      status: 'processing' as ContentStatus,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
      description: description,
      folderId: folderId === 'all' ? undefined : folderId,
      tags: tags.length > 0 ? tags : undefined,
      chatbots: linkedChatbots.length > 0 ? linkedChatbots : undefined,
      copilots: linkedCopilots.length > 0 ? linkedCopilots : undefined,
    }

    setIsSubmitting(true);

    try {
      await dispatch(createSnippet(contentData)).unwrap();
      toast({
        title: "Snippet ingestion started",
        description: "We'll update the library once processing finishes."
      });

      resetForm();
      onOpenChange(false);
    } catch (error) {
      toast({
        title: "Error creating snippet",
        description: "An error occurred while creating the snippet",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const resetForm = () => {
    setTitle('');
    setDescription('');
    setContent('');
    setTags([]);
    setIsLive(false);
    setLinkedChatbots([]);
    setLinkedCopilots([]);
    setIsAdvancedOpen(false);
    setFolderId('all'); // Reset to 'all' instead of null
  };

  return (
    <Dialog open={open} onOpenChange={(value) => {
      if (!value) resetForm();
      onOpenChange(value);
    }}>
      <DialogContent className="sm:max-w-xl lg:max-w-2xl bg-white dark:bg-gray-950 rounded-xl shadow-lg border border-gray-100 dark:border-gray-800 max-h-[90vh] overflow-auto">
        <DialogHeader className="space-y-3 pb-2 border-b dark:border-gray-800">
          <DialogTitle className="flex items-center gap-2 text-xl font-semibold text-primary">
            <FileText className="h-5 w-5 text-primary" />
            Create Content Snippet
          </DialogTitle>
          <p className="text-sm text-muted-foreground">
            Add knowledge-base content that powers your AI assistants
          </p>
        </DialogHeader>
        
        <Button
          variant="ghost"
          size="icon"
          className="absolute right-4 top-4 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          onClick={() => onOpenChange(false)}
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </Button>
        
        <form onSubmit={handleSubmit} className="space-y-6 py-3">
          {/* Basic Info Section */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="title" className="text-sm font-medium">
                  Title <span className="text-red-500">*</span>
                </Label>
                <Input 
                  id="title" 
                  value={title} 
                  onChange={(e) => setTitle(e.target.value)} 
                  placeholder="Enter a descriptive title"
                  className="focus:ring-primary/30 focus:border-primary"
                  required
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="description" className="text-sm font-medium">Description</Label>
                <Textarea 
                  id="description" 
                  value={description} 
                  onChange={(e) => setDescription(e.target.value)} 
                  placeholder="Add a brief description of this content"
                  className="h-20 resize-none focus:ring-primary/30 focus:border-primary"
                />
              </div>
              
              {/* Folder Selection */}
              <div className="space-y-2">
                <Label htmlFor="folder" className="text-sm font-medium">Folder</Label>
                <Select value={folderId || 'all'} onValueChange={(value) => setFolderId(value)}>
                  <SelectTrigger className="w-full focus:ring-primary/30 focus:border-primary">
                    <SelectValue placeholder="Select a folder" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectItem value="all">All Content</SelectItem>
                      {folders.map((folder) => (
                        <SelectItem key={folder.id} value={folder.id}>
                          <div className="flex items-center gap-2">
                            <Folder className="h-4 w-4 text-muted-foreground" />
                            <span>{folder.name}</span>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  Choose where to store this content
                </p>
              </div>

              
              
              <div className="space-y-2">
                <Label htmlFor="tags" className="text-sm font-medium">Tags</Label>
                <EnhancedTagsInput
                  tags={tags}
                  setTags={setTags}
                  availableTags={availableTags}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  Tags help organize and search for content
                </p>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="content" className="text-sm font-medium">
                  Content <span className="text-red-500">*</span>
                </Label>
                <div className="relative">
                  <Textarea 
                    id="content" 
                    value={content} 
                    onChange={(e) => setContent(e.target.value)} 
                    placeholder="Add your snippet content here"
                    className="min-h-[260px] resize-none focus:ring-primary/30 focus:border-primary"
                    required
                  />
                  <div className="absolute bottom-2 right-2 text-xs text-muted-foreground">
                    {content.length} characters
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  This content will be used to train your AI assistants
                </p>
              </div>
            </div>
          </div>
          
          {/* Advanced Options Section */}
          <Collapsible
            open={isAdvancedOpen}
            onOpenChange={setIsAdvancedOpen}
            className="border rounded-md p-4 bg-muted/10 transition-all duration-200"
          >
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium flex items-center gap-2">
                <Tag className="h-4 w-4 text-muted-foreground" />
                Advanced Options
              </h3>
              <CollapsibleTrigger asChild>
                <Button variant="ghost" size="sm" className="p-0 h-8 w-8 hover:bg-muted/20">
                  {isAdvancedOpen ? (
                    <ChevronUp className="h-4 w-4" />
                  ) : (
                    <ChevronDown className="h-4 w-4" />
                  )}
                  <span className="sr-only">Toggle advanced options</span>
                </Button>
              </CollapsibleTrigger>
            </div>
            
            <CollapsibleContent className="pt-4 space-y-6">
              <div className="flex items-center justify-between bg-muted/20 p-3 rounded-md">
                <div>
                  <Label htmlFor="isLive" className="text-sm font-medium">Publish Immediately</Label>
                  <p className="text-xs text-muted-foreground">Make snippet active after creation</p>
                </div>
                <Switch
                  id="isLive"
                  checked={isLive}
                  onCheckedChange={setIsLive}
                  disabled={isSubmitting}
                  className="data-[state=checked]:bg-green-500"
                />
              </div>
              
              <div className="space-y-3">
                <Label htmlFor="linkedChatbots" className="text-sm font-medium">Connect to Chatbots</Label>
                <EnhancedChatbotInput
                  chatbots={linkedChatbots}
                  setChatbots={setLinkedChatbots}
                  availableChatbots={availableChatbots}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  Choose which chatbots will have access to this content
                </p>
              </div>
              
              <div className="space-y-3">
                <Label htmlFor="linkedCopilots" className="text-sm font-medium">Connect to Copilots</Label>
                <EnhancedCopilotInput
                  copilots={linkedCopilots}
                  setCopilots={setLinkedCopilots}
                  availableCopilots={availableCopilots}
                  disabled={isSubmitting}
                />
                <p className="text-xs text-muted-foreground">
                  Choose which copilots will have access to this content
                </p>
              </div>
            </CollapsibleContent>
          </Collapsible>
          
          <DialogFooter className="pt-2 border-t dark:border-gray-800">
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)} 
              disabled={isSubmitting}
              className="border-gray-200 hover:bg-gray-50 dark:border-gray-700 dark:hover:bg-gray-800"
            >
              Cancel
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting}
              className="bg-primary hover:bg-primary/90 text-primary-foreground"
            >
              {isSubmitting ? "Creating..." : "Create Snippet"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
